'use client'

/**
 * SurfaceCloseButton — the canonical "X" used to dismiss a dialog,
 * sheet, palette, popover, or any other transient surface.
 *
 * Single source of truth for surface-close styling. Anywhere a user
 * would mentally call this "the close button," use this primitive.
 * Three semantic categories of X coexist in the codebase; only the
 * surface-close one belongs here:
 *
 *   A. Surface close (this primitive)        — 36×36 button, 18×18 icon
 *   B. Chip-internal remove (per-chip impl)  — ~12×12 icon
 *   C. Inline input clear (per-input impl)   — ~14×14 icon
 *
 * The size matches the embed's chat-header close (`.as-chat-header-btn-icon`)
 * so the @ordo/agent-embed morph palette and every Ordo dialog/sheet
 * present an identical X to the user. Sits within Linear/Stripe/Notion
 * desktop close-button norms (32–36px) and exceeds the WCAG 2.5.5
 * minimum touch target with margin.
 *
 * Designed to wrap Radix `*Primitive.Close` slots via `asChild`:
 *
 *   <SurfaceCloseButton asChild>
 *     <DialogPrimitive.Close />
 *   </SurfaceCloseButton>
 *
 * Without `asChild`, renders as a plain `<button type="button">` so
 * non-Radix surfaces (custom popovers, the embed's own portaled
 * morph) can drop it in directly.
 */

import { Slot } from '@radix-ui/react-slot'
import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'

export interface SurfaceCloseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Render as a child component (Radix slot pattern). Use to wrap
   *  `DialogPrimitive.Close`, `SheetPrimitive.Close`, etc., so the
   *  child's own ref/handlers/aria-* survive while the visual styling
   *  comes from this primitive. */
  asChild?: boolean
}

export const SurfaceCloseButton = React.forwardRef<HTMLButtonElement, SurfaceCloseButtonProps>(
  ({ className, asChild, children, onBlur, onClick, onPointerDown, ...props }, ref) => {
    const buttonRef = React.useRef<HTMLButtonElement | null>(null)
    const hadPointerDownRef = React.useRef(false)

    const setRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        buttonRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ;(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
        }
      },
      [ref],
    )

    const Comp: React.ElementType = asChild ? Slot : 'button'
    return (
      <Comp
        ref={setRef}
        type={asChild ? undefined : 'button'}
        aria-label={props['aria-label'] ?? 'Close'}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md',
          'text-muted-foreground transition-colors',
          'hover:bg-muted hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
        onBlur={(event: React.FocusEvent<HTMLButtonElement>) => {
          hadPointerDownRef.current = false
          onBlur?.(event)
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(event)
          if (event.defaultPrevented || !hadPointerDownRef.current) {
            return
          }
          const blurPointerFocus = () => {
            buttonRef.current?.blur()
            hadPointerDownRef.current = false
          }
          if (typeof window === 'undefined') {
            blurPointerFocus()
          } else {
            window.requestAnimationFrame(blurPointerFocus)
          }
        }}
        onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => {
          hadPointerDownRef.current = true
          onPointerDown?.(event)
        }}
        {...props}>
        {children ?? (
          <>
            <X className="h-[18px] w-[18px]" aria-hidden />
            <span className="sr-only">Close</span>
          </>
        )}
      </Comp>
    )
  },
)
SurfaceCloseButton.displayName = 'SurfaceCloseButton'
